/**
 * @file hooks/useClients.ts
 * @description 全局客戶名錄查詢 hook，供所有需要「客戶下拉」的表單共用。
 *
 * 後端端點：GET /api/clients（client.read，所有登入角色可讀）
 * 客戶數量級通常 < 1000，採一次性拉回 + TanStack Query 5 分鐘快取，
 * 多個頁面共享同一份資料，避免每打開一個 Modal 都重請求。
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/** 客戶下拉選項所需的最小欄位 */
export interface ClientItem {
  /** 客戶 ID（主鍵） */
  id: number;
  /** 客戶名稱 */
  name: string;
  /** 客戶編號（如 ZH-TAP-0001），可選顯示 */
  clientCode?: string | null;
}

/** 後端分頁回應結構 */
interface ClientListResponse {
  items: ClientItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** 共用的 query key，方便手動 invalidate */
export const CLIENTS_QUERY_KEY = ['clients-lookup'] as const;

/**
 * 取得客戶名錄（一次性拉回最多 500 筆，前端 Select 內過濾）。
 */
export function useClients(enabled = true) {
  return useQuery({
    queryKey: CLIENTS_QUERY_KEY,
    queryFn: () => apiClient.get<ClientListResponse>('/api/clients?pageSize=500'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
